import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import {
	withIsSiteAdmin,
	type WithIsSiteAdminProps,
} from '@confluence/change-edition/entry-points/higher-order.ts';
import { GradientCTAButton } from '@confluence/change-edition/entry-points/GradientCTAButton.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { OnboardingManualTrialModal } from '@confluence/premium-trial/entry-points/OnboardingManualTrialModal.ts';
import { fg } from '@confluence/feature-gating';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useShouldCheckEligibilityForSpotlight } from './useShouldCheckEligibilityForSpotlight.tsx';
import { setIsPremiumOnboardingHappened } from './isOnboardingHappenedUtils.ts';
import leftIllustration from './images/Illustration.svg';
import type { ListItemProps } from './ListItem.tsx';
import { ListItem } from './ListItem.tsx';
import { PremiumFeatureHighlightToggle } from './PremiumFeatureHighlightToggle.tsx';
import { usePremiumFeatureUsageIcon } from './usePremiumFeatureUsage.ts';
import { PremiumFeatureKey } from './useTrackPremiumFeatureUsage.ts';
import { PremiumTrialOnboardingSurvey } from './PremiumTrialOnboardingSurvey.tsx';
import { getIsTrialInLast15Days } from './getIsTrialInLast15Days.ts';
import { usePTOModalVisibilityOnReverseTrial } from './usePTOModalVisibilityOnReverseTrial.tsx';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from './useIsPremiumOnboardingFeatureDiscoveryEnabled.tsx';

export const i18n = defineMessages({
	modalTitle: {
		id: 'premium-onboarding.modal.title',
		description:
			'The text is shown as the main heading at the top of the Premium trial onboarding modal when a user opens the experience. It welcomes them into their Premium trial and introduces the guided feature tour. There are no placeholders in this message. Explains the purpose of the modal and trial.',
		defaultMessage: 'Welcome to your Premium trial',
	},
	modalBodyText: {
		id: 'premium-onboarding.modal.body.text',
		description:
			'The text is shown as the supporting body copy under the main heading in the Premium trial onboarding modal when a user first opens it. It invites them to explore Confluence’s most powerful features during their trial. There are no placeholders in this message. Explains the intent of the feature exploration.',
		defaultMessage: 'Explore Confluence’s most powerful features',
	},
	modalBodyTextPTO2: {
		id: 'premium-onboarding.modal.body.text.pto2',
		description:
			'The text is shown as the primary body copy in the Premium trial onboarding modal when the user is viewing the feature guide with all included features. It prompts them to get started by exploring the five most popular Premium features. There are no placeholders in this message. Explains how to begin the guided exploration.',
		defaultMessage: 'Get started by exploring our 5 most popular features.',
	},
	modalBodyTextPTO2WithoutAutomation: {
		id: 'premium-onboarding.modal.body.text.pto2.no.automation',
		description:
			'The text is shown as the primary body copy in the Premium trial onboarding modal when the user is viewing the feature guide for a plan variant without automation. It prompts them to get started by exploring the four most popular available features. There are no placeholders in this message. Explains how to begin exploring in this variant.',
		defaultMessage: 'Get started by exploring our 4 most popular features.',
	},
	modalTitleBeginningOfTrial: {
		id: 'premium-onboarding.modal-beginning-of-trial.title.pto2',
		description:
			'The text is shown as the main heading at the top of the Premium feature guide modal when a user opens it at the beginning of their trial or from an onboarding entry point. It tells them this view is a guide to key Premium features. There are no placeholders in this message. Explains what the modal is for.',
		defaultMessage: 'Your Premium feature guide',
	},
	modalTitleHalfwayThroughTrial: {
		id: 'premium-onboarding.modal-halfway-through-trial.title.pto2',
		description:
			"The text is shown as the main heading at the top of the Premium feature guide modal when a user is halfway through their trial. It tells them how many days are left in their trial. The placeholder daysLeft is substituted with the remaining number of trial days as a numeral, using singular 'day' when there is one and 'days' otherwise. Explains the trial time left in a celebratory tone.",
		defaultMessage:
			'You have {daysLeft, plural, one {# day} other {# days}} left on your Premium trial 🎉',
	},
	modalBodyTextBeginningOfTrial: {
		id: 'premium-onboarding.modal-beginning-of-trial.body.text.pto2',
		description:
			'The text is shown as the supporting body copy under the feature guide heading in the Premium trial onboarding modal when the user is at the beginning of their trial. It explains that each Premium feature will receive a checkmark once it has been used. There are no placeholders in this message. Explains how progress through the guide is tracked.',
		defaultMessage: 'Features will get a checkmark once you’ve used them.',
	},
	modalBodyTextHalfwayThroughTrialSentence1: {
		id: 'premium-onboarding.modal-halfway-through-trial.body.text.sentence1.pto2',
		description:
			'The text is shown as the first sentence of body copy in the Premium feature guide modal when a user is halfway through their trial and has used some Premium features. It tells them how many of the most popular features they’ve tried so far, with the number emphasized in bold. The placeholder count is substituted with the number of Premium features used as a numeral, and the placeholder b is substituted with bold formatting around that number. Explains current progress through key features.',
		defaultMessage: 'You’ve used <b>{count}</b> of Premium’s most popular features.',
	},
	modalBodyTextHalfwayThroughTrialSentence2: {
		id: 'premium-onboarding.modal-halfway-through-trial.body.text.sentence2.pto2',
		description:
			'The text is shown as the second sentence of body copy in the Premium feature guide modal when a user is halfway through their trial and has not used all features yet. It encourages them to get the most out of their trial by exploring the remaining Premium features. There are no placeholders in this message. Explains the next step to increase feature adoption.',
		defaultMessage: 'Get the most out of your trial by exploring features you haven’t tried yet.',
	},
	modalBodyTextHalfwayThroughTrialSentence3: {
		id: 'premium-onboarding.modal-halfway-through-trial.body.text.sentence3.pto2',
		description:
			'The text is shown as the body subtext in the Premium feature guide modal when a user is halfway through their trial and has already used all of the most popular Premium features. It confirms that they’ve completed the key part of the guide, with the word “all” emphasized in bold. The placeholder b is substituted with bold formatting around the word “all”. Explains full completion of the highlighted features.',
		defaultMessage: 'You’ve used <b>all</b> of Premium’s most popular features.',
	},
	toggleTitlePTO2: {
		id: 'premium-onboarding.toggle.title.pto2',
		description:
			'The text is shown as the label heading next to the Premium feature highlighting toggle in the onboarding modal when the user can enable or disable in-product highlights. It tells them that turning the toggle on will highlight Premium features across Confluence. There are no placeholders in this message. Explains what the toggle controls.',
		defaultMessage: 'Highlight Premium features in Confluence',
	},
	toggleBodyText: {
		id: 'premium-onboarding.toggle.body.text',
		description:
			'The text is shown as the supporting description under the Premium feature highlighting toggle in the onboarding modal when the toggle is visible. It explains that, if enabled, Confluence will mark Premium features with a label to make them easier to spot. There are no placeholders in this message. Explains the effect of turning the toggle on.',
		defaultMessage: 'We’ll help you identify Premium features with a label.',
	},
	buttonText: {
		id: 'premium-onboarding.button.text',
		description:
			'The text is shown on the primary call-to-action button at the bottom of the Premium trial onboarding modal when it is displayed. It prompts the user to open and explore the highlighted Premium features. There are no placeholders in this message. Explains the main action to continue the guide.',
		defaultMessage: 'Explore features',
	},
	analyticsTitle: {
		id: 'premium-onboarding.analytics.title',
		description:
			'The text is shown as the feature title label for Analytics in the list of Premium features inside the onboarding modal when the user is browsing the guide. It names the analytics capability being highlighted. There are no placeholders in this message. Explains which feature card is about analytics.',
		defaultMessage: 'Analytics',
	},
	analyticsBodyTextPTO2: {
		id: 'premium-onboarding.analytics.body.text.pto2',
		description:
			'The text is shown as the supporting body copy under the Analytics feature title in the Premium onboarding modal when the feature card is visible. It explains that Analytics lets users see engagement and views at the site, space, and content level. There are no placeholders in this message. Explains what insight this feature provides.',
		defaultMessage: 'See user engagement and views at the site, space, and content level.',
	},
	teamCalendarsTitle: {
		id: 'premium-onboarding.team-calendars.title',
		description:
			'The text is shown as the feature title label for Team calendars in the list of Premium features inside the onboarding modal when the user is browsing the guide. It names the team calendars capability being highlighted. There are no placeholders in this message. Explains which feature card is about calendars.',
		defaultMessage: 'Team calendars',
	},
	teamCalendarsBodyText: {
		id: 'premium-onboarding.team-calendars.body.text',
		description:
			'The text is shown as the supporting body copy under the Team calendars feature title in the Premium onboarding modal when that feature card is visible. It explains that this feature lets teams keep a single source of truth for schedules and due dates. There are no placeholders in this message. Explains the value of the calendar feature.',
		defaultMessage: 'Keep one single source of truth for your team’s schedules and due dates.',
	},
	unlimitedStorageAndWhiteboardsTitle: {
		id: 'premium-onboarding.unlimited-storage-and-whiteboards.title.pto2',
		description:
			'The text is shown as the feature title label for the Unlimited storage and whiteboards capability in the Premium onboarding modal when that feature card is visible. It names the combined benefit of extra storage and whiteboards. There are no placeholders in this message. Explains which feature card is about storage and whiteboards.',
		defaultMessage: 'Unlimited storage and whiteboards',
	},
	whiteboardBodyText: {
		id: 'premium-onboarding.whiteboard.body.text',
		description:
			'The text is shown as the supporting body copy under the Unlimited storage and whiteboards feature title in the Premium onboarding modal when that feature card is visible. It explains that users won’t run out of space to brainstorm and turn ideas into actions. There are no placeholders in this message. Explains the benefit of having ample whiteboard space.',
		defaultMessage: 'Never run out of space to turn ideas into actions.',
	},
	automationTitle: {
		id: 'premium-onboarding.automation.title',
		description:
			'The text is shown as the feature title label for Automation in the list of Premium features inside the onboarding modal when the user is browsing the guide. It names the automation capability being highlighted. There are no placeholders in this message. Explains which feature card is about automation.',
		defaultMessage: 'Automation',
	},
	automationBodyText: {
		id: 'premium-onboarding.automation.body.text',
		description:
			'The text is shown as the supporting body copy under the Automation feature title in the Premium onboarding modal when that feature card is visible. It explains that users can save time by creating rules that change content status, create Jira tasks, and perform other actions automatically. There are no placeholders in this message. Explains what kinds of workflows automation can handle.',
		defaultMessage:
			'Save time by creating rules to change content status, create a Jira task, and more.',
	},
	advancedPermissionsTitle: {
		id: 'premium-onboarding.advanced.permissions.title',
		description:
			'The text is shown as the feature title label for Advanced permissions in the list of Premium features inside the onboarding modal when the user is browsing the guide. It names the advanced permissions capability being highlighted. There are no placeholders in this message. Explains which feature card is about permissions.',
		defaultMessage: 'Advanced permissions',
	},
	advancedPermissionsBodyTextPTO2: {
		id: 'premium-onboarding.advanced.permissions.body.text.pto2',
		description:
			'The text is shown as the supporting body copy under the Advanced permissions feature title in the Premium onboarding modal when that feature card is visible. It explains that users can review permission-related issues and ensure their team has the right access to content. There are no placeholders in this message. Explains how this feature helps manage access.',
		defaultMessage:
			'Review permission-related issues and make sure your team has proper access to content.',
	},
	additionalFeaturesTitle: {
		id: 'premium-onboarding.title.additional.features',
		description:
			'The text is shown as a small heading above the list of extra Premium benefits at the bottom of the onboarding modal when additional features are presented. It introduces the short list of more capabilities included in Premium. There are no placeholders in this message. Explains that further benefits are listed below.',
		defaultMessage: 'Plus:',
	},
});

const NUMBER_OF_PREMIUM_FEATURES = 4;

const SURVEY_KEY = 'premium.trial.features.usefulness.survey';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Layout = styled.span({
	display: 'flex',
	flexDirection: 'row',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageContainer = styled.div({
	width: 220,
	minInlineSize: 'max-content',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextArea = styled.div({
	width: 500,
	backgroundColor: token('color.text.inverse'),
});

const textAreaBodyStyles = xcss({
	paddingRight: 'space.500',
	paddingBottom: 'space.300',
	paddingLeft: 'space.500',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SecondTitle = styled(Heading)({
	marginTop: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButton = styled.div({
	paddingRight: token('space.050'),
	paddingTop: token('space.200'),
	textAlign: 'right',
	minInlineSize: 'max-content',
});

const listItemWrapperStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	width: '100%',
	boxSizing: 'box-content',
	textAlign: 'right',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleWrapper = styled.div({
	width: '100%',
	boxSizing: 'box-content',
	display: 'flex',
	marginBottom: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleContent = styled.label({
	flexDirection: 'column',
});

const togglePaddingStyles = xcss({
	paddingTop: 'space.100',
	paddingRight: 'space.150',
});

export enum TriggerFrom {
	PILL = 'pill',
	ONBOARDING = 'onboarding',
	MID_TRIAL_ONBOARDING = 'midTrialOnboarding',
	HOME_ONBOARDING_TAKEOVER_V2 = 'homeOnboardingTakeoverV2',
}

type ModalComponentType = {
	onClose?: () => void;
	stop?: () => void;
	triggerFrom?: TriggerFrom;
	daysLeft?: number;
	source?: string;
} & WithIsSiteAdminProps;

const ModalComponent: FC<ModalComponentType> = ({
	isSiteAdmin,
	onClose,
	stop,
	triggerFrom,
	daysLeft = 0,
	source = 'premiumTrialOnboardingModal',
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const { Modal } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isSpotlightCheckEnabled }, { enableSpotlightCheck }] =
		useShouldCheckEligibilityForSpotlight();

	const [showSurvey, stopShowingSurvey] = useFeatureDiscovery(SURVEY_KEY);

	const { isPTORTEnabled, loading: isPTORTEnabledLoading } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled({
			shouldCheckIsInTrial: false,
		});

	const { showMidTrialModal: showPTORTMidTrialModal } = usePTOModalVisibilityOnReverseTrial({
		isPTORTEnabled,
		isSiteAdmin,
	});

	const modalRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(modalRef);

	const isTrialHalfWayThrough = isPTORTEnabled
		? showPTORTMidTrialModal
		: getIsTrialInLast15Days(daysLeft);

	const isTriggeredFromPillOrMidTrial = triggerFrom !== TriggerFrom.ONBOARDING;

	let modalBody = fg('a4c_free_and_standard_editions') ? (
		<FormattedMessage {...i18n.modalBodyTextPTO2WithoutAutomation} />
	) : (
		<FormattedMessage {...i18n.modalBodyTextPTO2} />
	);

	let modalTitle = i18n.modalTitle;

	if (isTriggeredFromPillOrMidTrial) {
		modalBody = <FormattedMessage {...i18n.modalBodyTextBeginningOfTrial} />;
		modalTitle = i18n.modalTitleBeginningOfTrial;
	}

	const sharedFeaturesMapper: Array<ListItemProps & { featureKey: PremiumFeatureKey }> = [
		{
			title: i18n.analyticsTitle,
			bodyText: i18n.analyticsBodyTextPTO2,
			icon: ChartTrendUpIcon,
			featureKey: PremiumFeatureKey.ANALYTICS,
		},
		{
			title: i18n.teamCalendarsTitle,
			bodyText: i18n.teamCalendarsBodyText,
			icon: CalendarIcon,
			featureKey: PremiumFeatureKey.TEAM_CALENDAR,
		},
		{
			title: i18n.unlimitedStorageAndWhiteboardsTitle,
			bodyText: i18n.whiteboardBodyText,
			icon: WhiteboardIcon,
			featureKey: PremiumFeatureKey.UNLIMITED_STORAGE,
		},
		{
			title: i18n.automationTitle,
			bodyText: i18n.automationBodyText,
			icon: CustomizeIcon,
			featureKey: PremiumFeatureKey.AUTOMATION,
		},
		{
			title: i18n.advancedPermissionsTitle,
			bodyText: i18n.advancedPermissionsBodyTextPTO2,
			icon: LockLockedIcon,
			featureKey: PremiumFeatureKey.ADVANCED_PERMISSIONS,
		},
	];

	if (fg('a4c_free_and_standard_editions')) {
		const automationIdx = sharedFeaturesMapper.findIndex(
			(feature) => feature.title === i18n.automationTitle,
		);
		sharedFeaturesMapper.splice(automationIdx, 1);
	}

	const { updatedSharedFeaturesMapperWithCheckIconStatus, totalFeaturesUsed, pto2Attributes } =
		usePremiumFeatureUsageIcon({
			shouldUpdate: isTriggeredFromPillOrMidTrial,
			rawSharedFeaturesMapper: sharedFeaturesMapper,
		});

	useEffect(() => {
		triggerFrom === TriggerFrom.ONBOARDING && setIsPremiumOnboardingHappened(true);
	}, [triggerFrom]);

	const areAllFeaturesUsed = totalFeaturesUsed === NUMBER_OF_PREMIUM_FEATURES;
	if (isTrialHalfWayThrough && isTriggeredFromPillOrMidTrial) {
		modalTitle = i18n.modalTitleHalfwayThroughTrial;
		modalBody = areAllFeaturesUsed ? (
			<FormattedMessage
				{...i18n.modalBodyTextHalfwayThroughTrialSentence3}
				values={{
					b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
				}}
			/>
		) : (
			<>
				<FormattedMessage
					{...i18n.modalBodyTextHalfwayThroughTrialSentence1}
					values={{
						count: totalFeaturesUsed,
						b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
					}}
				/>{' '}
				<FormattedMessage {...i18n.modalBodyTextHalfwayThroughTrialSentence2} />
			</>
		);
	}

	const closeModal = useCallback(
		async (actionSubjectId: any = '') => {
			actionSubjectId &&
				triggerFrom !== TriggerFrom.ONBOARDING &&
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId,
						source,
						attributes: {
							dwellTime: getDwellTime(),
							isPTORTEnabled,
							triggerFrom,
						},
					},
				}).fire();
			onClose?.();
			stop?.();
			setIsOpen(false);
			if (isSpotlightCheckEnabled === false && triggerFrom === TriggerFrom.ONBOARDING) {
				await enableSpotlightCheck();
			}
		},
		[
			createAnalyticsEvent,
			source,
			getDwellTime,
			isPTORTEnabled,
			triggerFrom,
			onClose,
			stop,
			isSpotlightCheckEnabled,
			enableSpotlightCheck,
		],
	);

	useEffect(() => {
		if (!isPTORTEnabledLoading && triggerFrom !== TriggerFrom.ONBOARDING) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: source,
					attributes: {
						isSiteAdmin,
						triggeredFrom: triggerFrom === TriggerFrom.PILL ? 'featureGuide' : triggerFrom,
						daysLeft,
						numPremiumFeaturesUsed: totalFeaturesUsed,
						...pto2Attributes,
						isPTORTEnabled,
					},
				},
			}).fire();
		}
		//making sure that this event gets fired only once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPTORTEnabledLoading]);

	const handleErrorBoundary = () => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'unhandledPremiumTrialError',
				source,
				actionSubject: 'ui',
				attributes: {
					attribution: Attribution.FLYWHEEL_ENGAGEMENT,
				},
			},
		}).fire();
	};

	if (triggerFrom === TriggerFrom.ONBOARDING) {
		return <OnboardingManualTrialModal onClose={closeModal} stop={stop} />;
	}

	return (
		<TransparentErrorBoundary
			attribution={Attribution.FLYWHEEL_ENGAGEMENT}
			onError={handleErrorBoundary}
		>
			<ModalTransition>
				{isOpen && (
					// To ensure that the close button isn't in focus when the modal is opened, disable auto focus on the modal
					<Modal
						onClose={() => closeModal('premiumTrialOnboardingModalCloseButton')}
						autoFocus={false}
						width={720}
					>
						<Layout ref={modalRef}>
							<ImageContainer>
								{/* Need a smaller height here for the image to make it adjustable based on height of the modal */}
								<Image
									src={leftIllustration}
									srcDark={leftIllustration}
									alt=""
									css={{
										objectFit: 'cover',
										minHeight: '100%',
										width: '220px',
										height: '400px !important',
									}}
								/>
							</ImageContainer>
							<TextArea>
								<CloseButton>
									<Button
										appearance="link"
										onClick={() => closeModal('premiumTrialOnboardingModalCloseButton')}
									>
										<CrossIcon label="Close Modal" color={token('color.text')} />
									</Button>
								</CloseButton>
								<Box xcss={textAreaBodyStyles}>
									<Heading size="medium" as="h1">
										<FormattedMessage {...modalTitle} values={{ daysLeft }} />
									</Heading>
									<SecondTitle as="h2" size={isTrialHalfWayThrough ? 'small' : 'xxsmall'}>
										{modalBody}
									</SecondTitle>
									<Box xcss={listItemWrapperStyles}>
										{Object.keys(updatedSharedFeaturesMapperWithCheckIconStatus)
											.filter(
												(key) =>
													!(
														updatedSharedFeaturesMapperWithCheckIconStatus[key].featureKey ===
															PremiumFeatureKey.UNLIMITED_STORAGE && isTriggeredFromPillOrMidTrial
													),
											)
											.map((key) => (
												<ListItem
													key={key}
													bodyText={updatedSharedFeaturesMapperWithCheckIconStatus[key].bodyText}
													checkIconStatus={
														updatedSharedFeaturesMapperWithCheckIconStatus[key].checkIconStatus
													}
													icon={updatedSharedFeaturesMapperWithCheckIconStatus[key].icon}
													title={updatedSharedFeaturesMapperWithCheckIconStatus[key].title}
												/>
											))}
									</Box>
									{/* We do not render toggle and CTA button in PTO Phase 2 when modal is triggered from Pill */}
									{isTriggeredFromPillOrMidTrial ? null : (
										<>
											<ToggleWrapper>
												<Box xcss={togglePaddingStyles}>
													<PremiumFeatureHighlightToggle
														id="prem-feature-highlight-toggle-modal"
														source="premTrialOnboardingModal"
														isPTORTEnabled={isPTORTEnabled}
													/>
												</Box>
												<ToggleContent htmlFor="prem-feature-highlight-toggle-modal">
													<Text as="p">
														<FormattedMessage {...i18n.toggleTitlePTO2} />
													</Text>
													<Text as="p" size="small">
														<FormattedMessage {...i18n.toggleBodyText} />
													</Text>
												</ToggleContent>
											</ToggleWrapper>

											<ButtonWrapper>
												<GradientCTAButton
													onClick={() => closeModal('premiumTrialOnboardingModalExploreFeatures')}
												>
													<FormattedMessage {...i18n.buttonText} />
												</GradientCTAButton>
											</ButtonWrapper>
										</>
									)}
									{isTriggeredFromPillOrMidTrial &&
										showSurvey &&
										isTrialHalfWayThrough &&
										totalFeaturesUsed > 0 &&
										!isPTORTEnabledLoading && (
											<PremiumTrialOnboardingSurvey
												trialDaysLeft={daysLeft}
												totalFeaturesUsed={totalFeaturesUsed}
												stopShowingSurvey={stopShowingSurvey}
												isSiteAdmin={isSiteAdmin}
												pto2Attributes={pto2Attributes}
												isPTORTEnabled={isPTORTEnabled}
											/>
										)}
								</Box>
							</TextArea>
						</Layout>
					</Modal>
				)}
			</ModalTransition>
		</TransparentErrorBoundary>
	);
};

export const PremiumTrialOnboardingModal = withIsSiteAdmin(ModalComponent);
